import React, { useMemo, useState } from 'react';
import { X, Upload, Loader2, CheckCircle2, ClipboardPaste } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const TARGETS = [
  ['email', 'Email'], ['name', 'Name'], ['phone', 'Phone'], ['whatsapp', 'WhatsApp'],
  ['street1', 'Street Address 1'], ['street2', 'Street Address 2'], ['city', 'City'],
  ['state', 'State'], ['zip', 'Zip'], ['country', 'Country'], ['tags', 'Tags'],
  ['alt_emails', 'Alt Emails'], ['alt_phones', 'Alt Phones'], ['lead_source', 'Source'], ['contact_notes', 'Notes'],
];

const GUESS = {
  email: /mail/i, name: /name/i, phone: /^phone|mobile|cell|contact number|phone_number/i,
  whatsapp: /whats/i, street1: /street.*1|address.*1|^address$/i, street2: /street.*2|address.*2/i,
  city: /city|town/i, state: /state|province|region/i, zip: /zip|postal|pin/i,
  country: /country|nation/i, tags: /tag|group|segment/i,
  alt_emails: /alt.*mail/i, alt_phones: /alt.*phone/i, lead_source: /source/i, contact_notes: /note/i,
};

// When auto-mapping "Name", prefer a real person-name column and never an ad/campaign/form name.
const NAME_PREFER = /full.?name|^name$|contact.?name|first.?name/i;
const NAME_AVOID = /ad_?name|adset|campaign|form_?name/i;

// Facebook/Meta Lead Ads quirks: phone values are prefixed with "p:"; sample rows use placeholders.
const cleanPhone = (v) => {
  const s = String(v || '').trim().replace(/^p:\s*/i, '').trim();
  return /<.*?>|dummy data for/i.test(s) ? '' : s;
};
const isDummyRow = (row, email) =>
  email === 'test@meta.com' || row.some((c) => /<test lead|dummy data for/i.test(String(c)));

// Parse CSV or tab-separated (Google Sheets paste). Handles quoted comma fields.
const parseTable = (text) => {
  const clean = text.replace(/\r\n/g, '\n').trim();
  if (!clean) return { headers: [], rows: [] };
  const lines = clean.split('\n').filter((l) => l.trim());
  const delim = (lines[0].match(/\t/g) || []).length >= (lines[0].match(/,/g) || []).length ? '\t' : ',';
  const splitLine = (line) => {
    if (delim === '\t') return line.split('\t').map((c) => c.trim());
    const out = []; let cur = ''; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === ',' && !q) { out.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1).map(splitLine);
  return { headers, rows };
};

const ContactImportModal = ({ open, onClose, onImported }) => {
  const { toast } = useToast();
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [map, setMap] = useState({});
  const [defaultTags, setDefaultTags] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [waOptIn, setWaOptIn] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fbCols, setFbCols] = useState(null);

  const applyRows = ({ headers, rows }) => {
    setParsed({ headers, rows });
    const m = {};
    Object.entries(GUESS).forEach(([target, re]) => {
      if (target === 'name') {
        // Prefer a person-name column (full_name); fall back to any "name" that isn't an ad/campaign name.
        let idx = headers.findIndex((h) => NAME_PREFER.test(h));
        if (idx < 0) idx = headers.findIndex((h) => re.test(h) && !NAME_AVOID.test(h));
        if (idx >= 0) m.name = String(idx);
        return;
      }
      const idx = headers.findIndex((h) => re.test(h));
      if (idx >= 0) m[target] = String(idx);
    });
    // Detect Facebook/Meta Lead Ads exports so we can capture the campaign/form each lead came from.
    const findIdx = (re) => headers.findIndex((h) => re.test(h));
    const campaign = findIdx(/^campaign_?name$/i);
    const form = findIdx(/^form_?name$/i);
    const isFb = campaign >= 0 || form >= 0 || findIdx(/^phone_number$/i) >= 0;
    setFbCols(isFb ? { campaign, form } : null);
    setMap(m);
    setResult(null);
  };

  const applyParse = (text) => applyRows(parseTable(text));

  const onPaste = (e) => { setRaw(e.target.value); applyParse(e.target.value); };
  const onFile = (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const reader = new FileReader();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      reader.onload = () => {
        try {
          const wb = XLSX.read(reader.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
          const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
          const rows = aoa.slice(1).map((r) => headers.map((_, i) => String(r[i] ?? '').trim()));
          setRaw('');
          applyRows({ headers, rows });
        } catch (err) {
          toast({ title: 'Could not read spreadsheet', description: 'Please check the file and try again.', variant: 'destructive' });
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => { setRaw(String(reader.result)); applyParse(String(reader.result)); };
      reader.readAsText(file);
    }
  };

  const contacts = useMemo(() => {
    const val = (row, target) => {
      const i = map[target]; return (i === undefined || i === '') ? '' : (row[Number(i)] || '').trim();
    };
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const seen = new Set();
    const out = [];
    parsed.rows.forEach((row) => {
      const email = val(row, 'email').toLowerCase();
      if (isDummyRow(row, email)) return; // skip Meta sample/test leads
      const phone = cleanPhone(val(row, 'phone'));
      const whatsapp = cleanPhone(val(row, 'whatsapp'));
      const validEmail = email && emailRe.test(email);
      // Keep the row if it has a valid email OR at least a phone/WhatsApp number.
      if (!validEmail && !phone && !whatsapp) return;
      if (validEmail) { if (seen.has(email)) return; seen.add(email); }
      const tagsRaw = val(row, 'tags');
      const tags = tagsRaw ? tagsRaw.split(/[;|]/).map((t) => t.trim()).filter(Boolean) : [];
      let leadSource = val(row, 'lead_source') || undefined;
      // Facebook Lead Ads: record which campaign/form the lead came from — as Source AND a tag.
      if (fbCols) {
        const campaign = fbCols.campaign >= 0 ? (row[fbCols.campaign] || '').trim() : '';
        const form = fbCols.form >= 0 ? (row[fbCols.form] || '').trim() : '';
        const parts = [campaign, form].filter(Boolean);
        if (parts.length && !leadSource) leadSource = parts.join(' / ');
        const campaignTag = campaign || form;
        if (campaignTag) tags.push(campaignTag);
      }
      out.push({
        email: validEmail ? email : undefined,
        name: val(row, 'name') || undefined, phone: phone || undefined, whatsapp: whatsapp || undefined,
        street1: val(row, 'street1') || undefined, street2: val(row, 'street2') || undefined,
        zip: val(row, 'zip') || undefined, city: val(row, 'city') || undefined,
        state: val(row, 'state') || undefined, country: val(row, 'country') || undefined,
        alt_emails: val(row, 'alt_emails') || undefined, alt_phones: val(row, 'alt_phones') || undefined,
        lead_source: leadSource, contact_notes: val(row, 'contact_notes') || undefined,
        tags: Array.from(new Set(tags)),
      });
    });
    return out;
  }, [parsed, map, fbCols]);

  const doImport = async () => {
    if (!contacts.length) { toast({ title: 'No valid contacts to import', description: 'Make sure an Email column is mapped.' }); return; }
    setImporting(true);
    try {
      const res = await api.post('/admin/crm/import', {
        contacts,
        default_tags: defaultTags.split(',').map((t) => t.trim()).filter(Boolean),
        sms_opt_in: smsOptIn, whatsapp_opt_in: waOptIn,
      });
      setResult(res.data);
      toast({ title: 'Import complete', description: `${res.data.created} added, ${res.data.updated} updated` });
      onImported && onImported();
    } catch (e) { toast({ title: 'Import failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setImporting(false); }
  };

  const reset = () => { setRaw(''); setParsed({ headers: [], rows: [] }); setMap({}); setResult(null); setFbCols(null); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" data-testid="import-contacts-modal">
      <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-2xl w-full p-6 space-y-4 my-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif-display text-2xl text-burgundy">Import contacts</h3>
            <p className="text-xs text-mahogany/70 mt-1">Paste from a Google Sheet or upload a CSV / Excel (.xlsx) file. Contacts are added to your CRM and become targetable for email{smsOptIn || waOptIn ? ', SMS & WhatsApp' : ''} campaigns.</p>
          </div>
          <button onClick={onClose} data-testid="import-close"><X className="w-5 h-5 text-mahogany/70" /></button>
        </div>

        {result ? (
          <div className="text-center py-8 space-y-3" data-testid="import-result">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p className="text-burgundy text-lg">Imported <b>{result.created}</b> new, updated <b>{result.updated}</b>{result.skipped ? `, skipped ${result.skipped}` : ''}.</p>
            <div className="flex justify-center gap-3">
              <button onClick={reset} className="pill-btn-outline text-sm">Import another</button>
              <button onClick={onClose} className="pill-btn text-sm">Done</button>
            </div>
          </div>
        ) : (
          <>
            {/* Input */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="pill-btn-outline text-sm cursor-pointer justify-center">
                <Upload className="w-4 h-4" /> Upload CSV / Excel
                <input type="file" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="import-file-input"
                  onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
              </label>
              <div className="text-xs text-mahogany/60 flex items-center gap-1"><ClipboardPaste className="w-3.5 h-3.5" /> …or paste rows below</div>
            </div>
            <textarea value={raw} onChange={onPaste} rows={4} data-testid="import-paste"
              placeholder="Paste here — include the header row (e.g. Name, Email, Phone, WhatsApp, City, Tags)"
              className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany font-mono" />

            {parsed.headers.length > 0 && (
              <>
                {fbCols && (
                  <div className="flex items-start gap-2 rounded-xl border border-mahogany/20 bg-mahogany/5 px-3 py-2.5 text-xs text-mahogany" data-testid="fb-detected-banner">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <span><b>Facebook Lead Ads detected.</b> Names &amp; phone numbers are cleaned automatically, sample/test leads are skipped, and each lead's campaign/form is saved as its <b>Source</b> and a <b>tag</b>.</span>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Map your columns</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {TARGETS.map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <span className="text-mahogany/80 w-24 shrink-0">{label}</span>
                        <select value={map[key] ?? ''} onChange={(e) => setMap((m) => ({ ...m, [key]: e.target.value }))}
                          data-testid={`map-${key}`}
                          className="flex-1 bg-white rounded-lg border border-rose/50 px-2 py-1.5 text-burgundy focus:outline-none">
                          <option value="">— none —</option>
                          {parsed.headers.map((h, i) => <option key={i} value={String(i)}>{h || `Column ${i + 1}`}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-mahogany/80">Add tag(s) to all (comma-separated)</span>
                    <input value={defaultTags} onChange={(e) => setDefaultTags(e.target.value)} placeholder="e.g. legacy, 2024-list"
                      data-testid="import-default-tags"
                      className="mt-1 w-full bg-white rounded-lg border border-rose/50 px-2 py-1.5 text-burgundy focus:outline-none" />
                  </label>
                  <div className="text-sm space-y-1.5">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)} data-testid="import-sms-optin" /> These contacts opted in to <b>SMS</b></label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={waOptIn} onChange={(e) => setWaOptIn(e.target.checked)} data-testid="import-wa-optin" /> These contacts opted in to <b>WhatsApp</b></label>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-rose-soft/30 rounded-xl px-4 py-3">
                  <span className="text-sm text-burgundy" data-testid="import-preview-count">
                    <b>{contacts.length}</b> valid contact(s) ready{contacts.length ? ` · e.g. ${contacts.slice(0, 2).map((c) => c.email || c.phone || c.whatsapp).join(', ')}` : ''}
                  </span>
                  <button onClick={doImport} disabled={importing || !contacts.length} data-testid="import-submit"
                    className="pill-btn text-sm disabled:opacity-60">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import {contacts.length || ''}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContactImportModal;
